import React, { useEffect, useMemo, useState } from "react";
import Modal from "../ui/Modal";
import SearchableSelect from "../ui/SearchableSelect";
import { useScheduleData } from "./ScheduleDataContext";
import { createSoftSchedule, fetchTicketsByProjectIds, getProjectIdFromTicket, getTicketSummary } from "../../api/client";
import { buildApiUrl, apiConfig } from "../../apiConfig";

function unwrapId(v) { if (v == null) return null; if (typeof v === 'object') return unwrapId(v.id ?? v.identifier ?? v.value ?? v.objectId ?? v.ticketId ?? v.projectId ?? null); return v; }
function extractProjectId(p) { return unwrapId(p?.id) ?? unwrapId(p?.projectId) ?? unwrapId(p?.project) ?? unwrapId(p?.['project/id']) ?? null; }
function extractProjectName(p) { return p?.name || p?.project?.name || `Project ${extractProjectId(p)}`; }
function extractTicketId(t) {
  const v = t?.id ?? t?.ticketId ?? t?.objectId ?? null;
  if (v && typeof v === 'object') return v.id ?? v.identifier ?? v.value ?? null;
  return v;
}
function toUtcIso(dateStr) {
  if (!dateStr) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr));
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}T00:00:00Z`;
}
function getTodayStr() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function NewSoftScheduleModal({ open, onClose, onCreated }) {
  const { token, projects, tickets, ticketProjectMap, backgroundLoading } = useScheduleData();

  // 'ticket' = pick project+ticket  |  'description' = free-text only
  const [mode, setMode] = useState('ticket');

  const [searchProject, setSearchProject] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [manualTicketId, setManualTicketId] = useState("");
  const [projectTickets, setProjectTickets] = useState([]);
  const [loadingProjectTickets, setLoadingProjectTickets] = useState(false);
  const [projectTicketsError, setProjectTicketsError] = useState(null);

  const [description, setDescription] = useState("");

  const [searchUser, setSearchUser] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [dateStart, setDateStart] = useState(getTodayStr);
  const [dateEnd, setDateEnd] = useState(getTodayStr);
  const [hours, setHours] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load users when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingUsers(true);
        const url = buildApiUrl(apiConfig.cwUsersPath, { conditions: "inactiveFlag=false" });
        const res = await fetch(url, { headers: { Accept: 'application/json', ...(token ? { Authorization: token } : {}) } });
        const data = res.ok ? await res.json() : [];
        const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        if (!cancelled) setUsers(list);
      } catch (e) {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, token]);

  // Fetch tickets for selected project
  useEffect(() => {
    if (!open) return;
    if (!selectedProjectId) { setProjectTickets([]); setProjectTicketsError(null); return; }
    let cancelled = false;
    (async () => {
      try {
        setProjectTicketsError(null);
        setLoadingProjectTickets(true);
        const list = await fetchTicketsByProjectIds([selectedProjectId], token, { pageSize: 200 });
        if (!cancelled) setProjectTickets(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) { setProjectTickets([]); setProjectTicketsError(e); }
      } finally {
        if (!cancelled) setLoadingProjectTickets(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, selectedProjectId, token]);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setDateStart(getTodayStr()); setDateEnd(getTodayStr());
    } else {
      setMode('ticket');
      setSearchProject(""); setSelectedProjectId(null); setSelectedTicketId(null);
      setManualTicketId(""); setProjectTickets([]); setProjectTicketsError(null);
      setDescription("");
      setSearchUser(""); setSelectedUser(null);
      setDateStart(getTodayStr()); setDateEnd(getTodayStr()); setHours(""); setError(null);
    }
  }, [open]);

  const filteredProjects = useMemo(() => {
    const n = searchProject.trim().toLowerCase();
    const arr = projects || [];
    if (!n) return arr;
    return arr.filter(p => {
      const id = String(extractProjectId(p) ?? "");
      const nm = (extractProjectName(p) || '').toLowerCase();
      return id.includes(n) || nm.includes(n);
    });
  }, [projects, searchProject]);

  function applyProjectSearch() {
    const raw = searchProject.trim();
    if (!raw) return;
    const exact = filteredProjects.find(p => String(extractProjectId(p)) === raw);
    let idToUse = null;
    if (exact) {
      idToUse = extractProjectId(exact);
    } else if (filteredProjects.length > 0) {
      idToUse = extractProjectId(filteredProjects[0]);
    } else if (/^\d+$/.test(raw)) {
      idToUse = raw;
    }
    if (!idToUse) return;
    setSelectedProjectId(idToUse);
    setSelectedTicketId(null);
    setManualTicketId("");
  }

  const ticketsForProject = useMemo(() => {
    if (!selectedProjectId) return [];
    const all = tickets || [];
    const scoped = all.filter(t => {
      const tid = extractTicketId(t);
      const pidFromMap = tid != null ? ticketProjectMap.get(tid) : null;
      if (pidFromMap != null) return String(pidFromMap) === String(selectedProjectId);
      const pid = getProjectIdFromTicket(t);
      return pid != null && String(pid) === String(selectedProjectId);
    });
    const remote = projectTickets || [];
    const seen = new Set();
    const merged = [];
    const pushIfNew = (t) => {
      const id = extractTicketId(t);
      const key = id != null ? String(id) : JSON.stringify(t);
      if (!seen.has(key)) { seen.add(key); merged.push(t); }
    };
    scoped.forEach(pushIfNew);
    remote.forEach(pushIfNew);
    return merged;
  }, [tickets, ticketProjectMap, selectedProjectId, projectTickets]);

  const filteredUsers = useMemo(() => {
    const n = searchUser.trim().toLowerCase();
    const arr = users || [];
    if (!n) return arr;
    return arr.filter(u => {
      const id = String(u?.id ?? "");
      const nm = (u?.name || u?.fullName || u?.identifier || '').toLowerCase();
      return id.includes(n) || nm.includes(n);
    });
  }, [users, searchUser]);

  async function onSubmit() {
    try {
      setError(null);
      if (mode === 'ticket') {
        if (!selectedProjectId) throw new Error("Select a project");
        if (!selectedTicketId) throw new Error("Select a ticket");
      } else {
        if (!description.trim()) throw new Error("Enter a description");
      }
      if (!selectedUser) throw new Error("Select a user");
      if (!dateStart || !dateEnd) throw new Error("Select a date range");
      const ds = toUtcIso(dateStart);
      const de = toUtcIso(dateEnd);
      if (!ds || !de) throw new Error("Invalid date format");
      if (new Date(de) < new Date(ds)) throw new Error("End date must be after start date");

      const memberIdentifier = selectedUser?.identifier || String(selectedUser?.id ?? "");
      if (!memberIdentifier) throw new Error("Selected user has no identifier");

      let ticketIdNum = null;
      let projectInfo = null;
      let ticketInfo = null;
      let descriptionValue = null;

      if (mode === 'ticket') {
        ticketIdNum = (() => {
          const s = String(selectedTicketId ?? '').trim();
          return /^\d+$/.test(s) ? Number(s) : selectedTicketId;
        })();
        // Build projectInfo: "<id> • <name>"
        const projObj = (projects || []).find(p => String(extractProjectId(p)) === String(selectedProjectId));
        const projName = projObj ? extractProjectName(projObj) : `Project ${selectedProjectId}`;
        projectInfo = `${selectedProjectId} • ${projName}`;
        // Build ticketInfo: "RR<projectId> <summary>"
        const ticketObj = ticketsForProject.find(t => String(extractTicketId(t)) === String(selectedTicketId))
          || (projectTickets || []).find(t => String(extractTicketId(t)) === String(selectedTicketId));
        const ticketSummary = ticketObj ? getTicketSummary(ticketObj) || '' : '';
        ticketInfo = `RR${selectedProjectId}${ticketSummary ? ` ${ticketSummary}` : ''}`;
      } else {
        descriptionValue = description.trim();
      }

      setSubmitting(true);
      const payload = {
        memberIdentifier,
        dateStart: ds,
        dateEnd: de,
        ...(hours !== '' && Number(hours) > 0 ? { hours: Number(hours) } : {}),
        ...(ticketIdNum != null ? { ticketId: ticketIdNum } : {}),
        ...(projectInfo ? { projectInfo } : {}),
        ...(ticketInfo ? { ticketInfo } : {}),
        ...(descriptionValue ? { description: descriptionValue } : {}),
      };
      const result = await createSoftSchedule(payload, token);
      const newEntry = {
        ...payload,
        ...(result || {}),
      };
      onCreated?.(newEntry);
      onClose?.();
    } catch (e) {
      setError(e);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <Modal>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Add Soft Schedule</h3>
        <button onClick={() => onClose?.()} disabled={submitting}>Close</button>
      </div>
      <div style={{ marginTop: 12, display: 'grid', gap: 12, color: 'var(--white_coordinating, #ffffff)' }}>
        {error && <div style={{ color: '#ffb3b3' }}>Error: {error.message}</div>}

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setMode('ticket')}
            style={{ flex: 1, padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc', background: mode === 'ticket' ? '#1976d2' : '#fff', color: mode === 'ticket' ? '#fff' : '#000', fontWeight: mode === 'ticket' ? 700 : 400, cursor: 'pointer' }}
          >
            Pick Project / Ticket
          </button>
          <button
            onClick={() => setMode('description')}
            style={{ flex: 1, padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc', background: mode === 'description' ? '#1976d2' : '#fff', color: mode === 'description' ? '#fff' : '#000', fontWeight: mode === 'description' ? 700 : 400, cursor: 'pointer' }}
          >
            Description Only
          </button>
        </div>

        {/* Ticket mode: Project + Ticket pickers */}
        {mode === 'ticket' && (<>
          {/* Project */}
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Project</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                placeholder="Search project id or name"
                value={searchProject}
                onChange={e => setSearchProject(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyProjectSearch(); } }}
                style={{ flex: 1, padding: '6px 8px', background: '#ffffff', color: '#000', border: '1px solid #ccc', borderRadius: 4 }}
              />
              <button onClick={applyProjectSearch} style={{ background: '#ffffff', color: '#000', border: '1px solid #ccc', borderRadius: 4, padding: '6px 10px' }}>Search</button>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#ddd' }}>Press Enter or click Search to select by ID.</div>
            <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid #ccc', background: '#fff', color: '#000' }}>
              {filteredProjects.slice(0, 200).map(p => {
                const id = extractProjectId(p);
                const label = `${id} • ${extractProjectName(p)}`;
                const checked = String(selectedProjectId) === String(id);
                return (
                  <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px' }}>
                    <input type="radio" name="soft_proj" checked={checked} onChange={() => { setSelectedProjectId(id); setSelectedTicketId(null); }} />
                    <span style={{ color: '#000' }}>{label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Ticket */}
          <div style={{ opacity: selectedProjectId ? 1 : 0.4, pointerEvents: selectedProjectId ? 'auto' : 'none', transition: 'opacity 160ms' }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Ticket</div>
            <SearchableSelect
              disabled={!selectedProjectId}
              items={ticketsForProject.slice(0, 1000).map(t => ({
                value: extractTicketId(t),
                label: `${extractTicketId(t)} • ${getTicketSummary(t) || ''}`,
              }))}
              value={selectedTicketId}
              onChange={val => setSelectedTicketId(val)}
              placeholder="Search tickets by id or summary"
            />
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Or enter Ticket ID (e.g. 12345)"
                value={manualTicketId}
                onChange={e => setManualTicketId(e.target.value)}
                disabled={!selectedProjectId}
                style={{ flex: 1, minWidth: 220, padding: '6px 8px', background: '#ffffff', color: '#000', border: '1px solid #ccc', borderRadius: 4 }}
              />
              <button
                type="button"
                onClick={() => {
                  const v = String(manualTicketId || '').trim();
                  if (!v) return;
                  setSelectedTicketId(/^\d+$/.test(v) ? Number(v) : v);
                }}
                disabled={!selectedProjectId || !String(manualTicketId || '').trim()}
                style={{ background: '#ffffff', color: '#000', border: '1px solid #ccc', borderRadius: 4, padding: '6px 10px' }}
              >
                Use ID
              </button>
            </div>
            {selectedProjectId && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#ddd' }}>
                {loadingProjectTickets ? (
                  <span>Fetching tickets for project {String(selectedProjectId)}…</span>
                ) : projectTicketsError ? (
                  <span style={{ color: '#ffb3b3' }}>Failed to fetch project tickets</span>
                ) : (
                  <span>
                    Showing {ticketsForProject.length} tickets for project {String(selectedProjectId)}
                    {projectTickets.length ? ` • ${projectTickets.length} loaded from API` : ''}
                  </span>
                )}
                {backgroundLoading && <span> • still loading in background</span>}
              </div>
            )}
          </div>
        </>)}

        {/* Description mode */}
        {mode === 'description' && (
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Description <span style={{ fontWeight: 400, color: '#ddd', fontSize: 12 }}>(shown on bar and tooltip)</span></div>
            <input
              type="text"
              placeholder="e.g. On-call coverage, Buffer time…"
              value={description}
              onChange={e => setDescription(e.target.value)}
              style={{ width: '100%', padding: '6px 8px', background: '#ffffff', color: '#000', border: '1px solid #ccc', borderRadius: 4 }}
            />
          </div>
        )}

        {/* User — always required */}
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>User</div>
          <input
            placeholder="Search user id or name"
            value={searchUser}
            onChange={e => setSearchUser(e.target.value)}
            style={{ width: '100%', padding: '6px 8px', marginBottom: 6, background: '#ffffff', color: '#000', border: '1px solid #ccc', borderRadius: 4 }}
          />
          <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid #ccc', background: '#fff', color: '#000' }}>
            {loadingUsers && <div style={{ padding: 8 }}>Loading users…</div>}
            {!loadingUsers && filteredUsers.slice(0, 300).map(u => {
              const id = u?.id;
              const name = u?.name || u?.fullName || u?.identifier || `User ${id}`;
              const label = `${id ?? ''} ${id ? '• ' : ''}${name}`;
              const checked = selectedUser?.id === id;
              return (
                <label key={id ?? name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px' }}>
                  <input type="radio" name="soft_user" checked={checked} onChange={() => setSelectedUser(u)} />
                  <span style={{ color: '#000' }}>{label}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Dates */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Start date</div>
            <input type="date" value={dateStart} onChange={e => { setDateStart(e.target.value); setDateEnd(e.target.value); }} style={{ background: '#ffffff', color: '#000', border: '1px solid #ccc', borderRadius: 4, padding: '4px 6px' }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>End date</div>
            <input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)} style={{ background: '#ffffff', color: '#000', border: '1px solid #ccc', borderRadius: 4, padding: '4px 6px' }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Hours <span style={{ fontWeight: 400, color: '#ddd', fontSize: 12 }}>(optional)</span></div>
            <input
              type="number" min="0" step="0.5" placeholder="e.g. 8"
              value={hours}
              onChange={e => setHours(e.target.value)}
              style={{ width: 90, padding: '4px 6px', background: '#ffffff', color: '#000', border: '1px solid #ccc', borderRadius: 4 }}
            />
          </div>
        </div>

        <button
          onClick={onSubmit}
          disabled={submitting || !selectedUser || !dateStart || !dateEnd || (mode === 'ticket' ? (!selectedProjectId || !selectedTicketId) : !description.trim())}
          style={{ background: '#ffffff', color: '#000', border: '1px solid #ccc', borderRadius: 4, padding: '6px 10px' }}
        >
          {submitting ? 'Creating…' : 'Create Soft Schedule'}
        </button>
      </div>
    </Modal>
  );
}
